import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Globe,
  LogOut,
  Map,
  SlidersHorizontal,
  TrendingUp,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { isAuthEnabled, logout, username } from '../auth/keycloak';
import { LANGS, useLang, t } from '../i18n';
import { useTheme } from './theme-provider';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '@/lib/utils';

const VIEWS = [
  { to: '/', label: () => t.navOverview, icon: BarChart3 },
  { to: '/trends', label: () => t.navTrends, icon: TrendingUp },
  { to: '/benchmarking', label: () => t.navBenchmarking, icon: Globe },
  { to: '/map', label: () => t.navMap, icon: Map },
];

/** Keeps --nav-h (content offset, drawer top) equal to the nav's real height, which varies
 *  with the breakpoint (one or two rows) and the script (Arabic lines are taller). */
function useNavHeight() {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement.style;
    const sync = () => root.setProperty('--nav-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.removeProperty('--nav-h');
    };
  }, []);
  return ref;
}

export function Nav({
  menuOpen,
  onToggleMenu,
  hasActiveFilters = false,
}: {
  menuOpen: boolean;
  onToggleMenu: () => void;
  hasActiveFilters?: boolean;
}) {
  const ref = useNavHeight();
  const location = useLocation();
  const activeLinkRef = useRef<HTMLAnchorElement | null>(null);
  const { lang, setLang } = useLang();
  const { theme, setTheme } = useTheme();
  const user = username();
  const initial = user ? user.charAt(0).toUpperCase() : 'U';

  // Smoothly center the active navigation tab on mobile when route changes
  useEffect(() => {
    if (activeLinkRef.current) {
      activeLinkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [location.pathname]);

  return (
    <nav className="nav" aria-label={t.primaryNav} ref={ref}>
      <a className="wordmark shrink-0" href="/">
        <img
          src="/logo.png"
          alt="APUA – ASEA"
          className="h-7 w-7 object-contain shrink-0"
        />
        <span className="whitespace-nowrap flex items-baseline">
          <span>APUA – ASEA</span>
          <small className="hidden sm:inline">UPBEAT Database</small>
        </span>
      </a>

      <div className="links">
        {VIEWS.map((v) => {
          const Icon = v.icon;
          const isCurrent = v.to === '/' ? location.pathname === '/' : location.pathname === v.to;
          return (
            <NavLink
              key={v.to}
              to={{ pathname: v.to, search: window.location.search }}
              end={v.to === '/'}
              ref={isCurrent ? activeLinkRef : undefined}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 transition-colors ${isActive ? 'on font-semibold' : ''}`
              }
            >
              <Icon className="h-4 w-4 opacity-75 shrink-0" />
              <span>{v.label()}</span>
            </NavLink>
          );
        })}
      </div>

      {/* Actions: User Account Dropdown and Filter drawer button grouped together */}
      <div className="nav-actions flex items-center gap-2 ml-auto shrink-0">
        <div className="account shrink-0">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-[34px] flex items-center gap-2 rounded-md border border-hair bg-glass-2 px-2.5 text-xs text-txt transition-all hover:border-gold-a28 hover:bg-glass-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold cursor-pointer"
                aria-label={user || 'User Menu'}
              >
                <Avatar className="h-6 w-6 border-hair">
                  <AvatarFallback>{initial}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline-block max-w-[120px] truncate font-medium text-mut hover:text-txt">
                  {user || 'Analyst'}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 p-1.5">
              <DropdownMenuLabel className="flex items-center gap-2.5 pb-2">
                <Avatar className="h-7 w-7 border-hair">
                  <AvatarFallback>{initial}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0">
                  <span className="font-semibold text-xs text-txt truncate">
                    {user || 'Utility Analyst'}
                  </span>
                  <span className="text-[10px] text-dim truncate">
                    UPBEAT Benchmarking
                  </span>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              {/* Direct 1-click Language Switcher */}
              <div className="px-2 py-2">
                <div className="flex items-center pb-2 px-0.5">
                  <span className="text-[11px] font-semibold text-dim uppercase tracking-wider flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 text-gold" />
                    {t.language}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setLang(l.code);
                      }}
                      className={cn(
                        'py-1 px-1 rounded-md text-xs font-semibold transition-all cursor-pointer text-center',
                        lang === l.code
                          ? 'bg-gold text-ink font-bold shadow-sm'
                          : 'bg-glass-2 border border-hair text-mut hover:text-txt hover:bg-glass-hover hover:border-hair2'
                      )}
                      title={l.name}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <DropdownMenuSeparator />

              {/* Direct 1-click Theme Switcher */}
              <div className="px-2 py-2">
                <div className="flex items-center pb-2 px-0.5">
                  <span className="text-[11px] font-semibold text-dim uppercase tracking-wider flex items-center gap-1.5">
                    <Sun className="h-3.5 w-3.5 text-gold" />
                    {t.theme}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setTheme('light');
                    }}
                    className={cn(
                      'py-1 px-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-1',
                      theme === 'light'
                        ? 'bg-gold text-ink font-bold shadow-sm'
                        : 'bg-glass-2 border border-hair text-mut hover:text-txt hover:bg-glass-hover hover:border-hair2'
                    )}
                    title={t.themeLight}
                  >
                    <Sun className="h-3 w-3" />
                    <span>{t.themeLight}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setTheme('dark');
                    }}
                    className={cn(
                      'py-1 px-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-1',
                      theme === 'dark'
                        ? 'bg-gold text-ink font-bold shadow-sm'
                        : 'bg-glass-2 border border-hair text-mut hover:text-txt hover:bg-glass-hover hover:border-hair2'
                    )}
                    title={t.themeDark}
                  >
                    <Moon className="h-3 w-3" />
                    <span>{t.themeDark}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setTheme('system');
                    }}
                    className={cn(
                      'py-1 px-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-1',
                      theme === 'system'
                        ? 'bg-gold text-ink font-bold shadow-sm'
                        : 'bg-glass-2 border border-hair text-mut hover:text-txt hover:bg-glass-hover hover:border-hair2'
                    )}
                    title={t.themeSystem}
                  >
                    <Monitor className="h-3 w-3" />
                    <span>{t.themeSystem}</span>
                  </button>
                </div>
              </div>

              {isAuthEnabled() && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-bad hover:bg-bad/10 hover:text-bad cursor-pointer gap-2 py-2"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    <span>{t.signOut}</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Filter drawer toggle button */}
        <Button
          type="button"
          variant={menuOpen ? 'default' : 'outline'}
          size="sm"
          className={`menu${menuOpen ? ' on' : ''} h-[34px] shrink-0 relative flex items-center gap-1.5 px-3 text-xs`}
          aria-expanded={menuOpen}
          aria-controls="filters"
          onClick={onToggleMenu}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span>{t.filtersButton}</span>
          {hasActiveFilters && (
            <span className="relative flex h-2 w-2 ml-0.5" title={t.filtersActive}>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
            </span>
          )}
        </Button>
      </div>
    </nav>
  );
}
